import { Fragment, useEffect, useRef, type ComponentProps, type CSSProperties, type SyntheticEvent } from 'react'
import { Button, Tag, Text } from '@grovus/design-system'
import type { Axis, HypothesisValue } from '../hypotheses/dictionaryApi'
import type { Decision } from './api'
import type { Suggestion } from './contract'
import { DictionaryPicker } from './DictionaryPicker'
import { fill } from './format'
import { axisCopy, DICTIONARY_COPY } from '../i18n'
import type { DictionaryPhase } from './useDictionary'
import type { Row } from './partition'
import { labelOf, type ViewContext } from './view'

/** The chip of one row whose picker is open. One chip is open at a time. */
export interface OpenChip {
  rowId: string
  axis: Axis
}

/**
 * In-cell assignment (GRO-170), passed only to a reader whose role may assign. The screen
 * owns the open chip, the options of each axis and the writes.
 */
export interface Tagging {
  /** The rows answer the current scope and no write is out for this company. */
  enabled: boolean
  open: OpenChip | null
  onOpen: (chip: OpenChip) => void
  onClose: () => void
  /** The assignable values of an axis, empty until its first read answered. */
  values: (axis: Axis) => HypothesisValue[]
  /** A read of the axis answered, so its picker can offer values. */
  fresh: (axis: Axis) => boolean
  /** Where the axis's read stands; `failed` shows the failure in the open chip. */
  phase: (axis: Axis) => DictionaryPhase
  retry: (axis: Axis) => void
  /** A value was chosen for the row's axis, or the axis was cleared with `null`. */
  onChoose: (rowId: string, axis: Axis, id: string | null) => void
  onCreate: (rowId: string, axis: Axis, name: string) => void
  /** An open suggestion of the row was accepted or rejected (GRO-173). */
  onDecide: (rowId: string, suggestion: Suggestion, decision: Decision) => void
}

/**
 * A press or a key inside a chip, or inside its portalled dropdown, stays with the chip:
 * React bubbles a portalled event through the component tree, so the row would otherwise
 * open the detail on it (lesson frontend-l9).
 */
const keep = (event: SyntheticEvent) => event.stopPropagation()

const ROW: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }
/** A chip is a plain button: the design system exports no unstyled one. */
const BUTTON: CSSProperties = {
  padding: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit', textAlign: 'start',
}
const EMPTY: CSSProperties = {
  padding: '0 8px',
  lineHeight: '20px',
  fontSize: 12,
  borderRadius: 'var(--mantine-radius-sm)',
  border: '1px dashed light-dark(var(--mantine-color-gray-5), var(--mantine-color-dark-3))',
  color: 'light-dark(var(--mantine-color-gray-7), var(--mantine-color-dark-1))',
}

const REJECT: CSSProperties = { ...BUTTON, padding: '0 4px', lineHeight: '20px', fontSize: 12 }

/**
 * The open chip's picker, focused once its options answered: the field is disabled while
 * they load, and a disabled field takes no focus, so `autoFocus` alone would leave Escape
 * and a click outside with nothing to close.
 */
function ChipPicker(props: ComponentProps<typeof DictionaryPicker>) {
  const box = useRef<HTMLSpanElement>(null)
  const ready = props.disabled !== true
  useEffect(() => {
    if (ready) box.current?.querySelector('input')?.focus()
  }, [ready])
  return <span ref={box}><DictionaryPicker {...props} /></span>
}

interface Props {
  row: Row
  ctx: ViewContext
  /** Absent for a reader who cannot assign, and in the grid: the chips are plain text. */
  tagging?: Tagging
}

/** The axes after the idea, in the order their chips follow it. */
const TAG_AXES = ['visual', 'message', 'offer'] as const

/**
 * The «Идея и теги» cell (GRO-170, GRO-171): the idea chip, bold, with its version letter,
 * then the Visual, Message and Offer chips. A reader who can assign gets a dashed «+ идея»,
 * «+ визуал», «+ сообщение» or «+ оффер» for an empty axis and opens a picker from any
 * chip; a reader who cannot sees the idea's muted «идея не назначена» and no chip for
 * another axis when none is set.
 *
 * An empty axis with an open suggestion (GRO-173) shows, for a reader who can assign, a
 * dashed «Grovus предлагает: …» that accepts and a «✕» that rejects, in place of the add
 * chip; the first suggestion of the axis in the served order. A reader who cannot assign
 * cannot act on one, so sees none.
 */
export function TagChips({ row, ctx, tagging }: Props) {
  const { t, lang } = ctx
  const idea = labelOf(ctx.dimensions.hypotheses, row.hypothesisId, lang)
  const ideaText = idea === null ? null : row.versionLabel !== null ? `${idea} · ${row.versionLabel}` : idea
  const tags = {
    visual: labelOf(ctx.dimensions.techniques, row.techniqueId, lang),
    message: labelOf(ctx.dimensions.messages, row.messageId, lang),
    offer: labelOf(ctx.dimensions.offers, row.offerId, lang),
  }
  const names: Record<Axis, string> = { idea: t.hypothesis, visual: t.technique, message: t.message, offer: t.offer }
  const adds: Record<Axis, string> = {
    idea: t.chipAddIdea, visual: t.chipAddVisual, message: t.chipAddMessage, offer: t.chipAddOffer,
  }

  if (tagging === undefined) {
    return (
      <span style={ROW}>
        <Text size="sm" fw={idea === null ? undefined : 600} className={idea === null ? 'grv-muted' : undefined} component="span">
          {ideaText ?? t.hypUnassigned}
        </Text>
        {TAG_AXES.map((axis) => tags[axis] !== null && <Tag key={axis}>{tags[axis]}</Tag>)}
      </span>
    )
  }

  const chip = (axis: Axis, label: string | null, set: boolean) => {
    const opened = tagging.open !== null && tagging.open.rowId === row.id && tagging.open.axis === axis
    const name = names[axis]
    const suggestion = set ? undefined : row.suggestions.find((s) => s.field === axis)
    if (!opened && suggestion !== undefined) {
      return (
        <span style={ROW}>
          <button
            type="button"
            style={{ ...BUTTON, cursor: tagging.enabled ? 'pointer' : 'default' }}
            aria-label={`${name}: ${t.suggestAccept}`}
            disabled={!tagging.enabled}
            onClick={() => tagging.onDecide(row.id, suggestion, 'accept')}
            data-testid={`suggest-${axis}-${row.id}`}
          >
            <span style={EMPTY}>{fill(t.suggestChip, { value: suggestion.value })}</span>
          </button>
          <button
            type="button"
            style={{ ...REJECT, cursor: tagging.enabled ? 'pointer' : 'default' }}
            aria-label={`${name}: ${t.suggestReject}`}
            disabled={!tagging.enabled}
            onClick={() => tagging.onDecide(row.id, suggestion, 'reject')}
            data-testid={`suggest-reject-${axis}-${row.id}`}
          >
            ✕
          </button>
        </span>
      )
    }
    if (opened && tagging.phase(axis) === 'failed') {
      const copy = DICTIONARY_COPY[lang]
      return (
        <span style={ROW} role="alert" data-testid={`chip-failed-${axis}-${row.id}`}>
          <Text size="xs" component="span">{axisCopy(copy, axis).optionsFailed}</Text>
          <Button size="compact-xs" variant="default" onClick={() => tagging.retry(axis)}>{copy.retry}</Button>
          <Button size="compact-xs" variant="subtle" onClick={tagging.onClose}>{copy.cancel}</Button>
        </span>
      )
    }
    if (opened) {
      return (
        <ChipPicker
          lang={lang}
          axis={axis}
          aria-label={name}
          values={tagging.values(axis)}
          value={null}
          onChoose={(id) => tagging.onChoose(row.id, axis, id)}
          onCreate={(typed) => tagging.onCreate(row.id, axis, typed)}
          clearLabel={set ? t.chipClear : undefined}
          disabled={!tagging.enabled || !tagging.fresh(axis)}
          defaultDropdownOpened
          onDropdownClose={tagging.onClose}
          size="xs"
          w={200}
          data-testid={`chip-picker-${axis}-${row.id}`}
        />
      )
    }
    return (
      <button
        type="button"
        style={{ ...BUTTON, cursor: tagging.enabled ? 'pointer' : 'default' }}
        aria-label={`${name}: ${label ?? adds[axis]}`}
        disabled={!tagging.enabled}
        onClick={() => tagging.onOpen({ rowId: row.id, axis })}
        data-testid={`chip-${axis}-${row.id}`}
      >
        {label === null ? (
          <span style={EMPTY}>{adds[axis]}</span>
        ) : axis === 'idea' ? (
          <Text size="sm" fw={600} component="span">{label}</Text>
        ) : (
          <Tag>{label}</Tag>
        )}
      </button>
    )
  }

  return (
    <span style={ROW} onClick={keep} onKeyDown={keep} onKeyUp={keep}>
      {chip('idea', ideaText, idea !== null)}
      {TAG_AXES.map((axis) => <Fragment key={axis}>{chip(axis, tags[axis], tags[axis] !== null)}</Fragment>)}
    </span>
  )
}
